import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { walletApi, subscriptionsApi, subscriptionTypesApi, userApi, authApi } from '../lib/api';
import { formatCurrency, formatDate, getTimeRemaining } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import logoIzyko from '../assets/logos/izyko-black.png';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Wallet, CreditCard, Tv, User, LogOut, Copy, Check, Home,
  Loader2, ArrowUpRight, ArrowDownLeft, Clock, Plus, ChevronRight, Menu, X,
  MessageCircle, KeyRound, Mail, Eye, EyeOff, AlertTriangle
} from 'lucide-react';

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { user, logout, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('home');
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Dialogs
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [codesMap, setCodesMap] = useState({});

  // Security dialogs
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailStep, setEmailStep] = useState('request'); // 'request' or 'verify'
  const [emailCode, setEmailCode] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const navItems = [
    { id: 'home', icon: Home, label: 'Accueil' },
    { id: 'browse', icon: Tv, label: 'Offres' },
    { id: 'transactions', icon: Wallet, label: 'Transactions' },
    { id: 'profile', icon: User, label: 'Profil' }
  ];

  useEffect(() => {
    fetchData();
    fetchDepartments();
    const params = new URLSearchParams(location.search);
    const subscribeId = params.get('subscribe');
    if (subscribeId) handleShowSubscribe(subscribeId);
    const transactionId = params.get('transactionId');
    if (transactionId) {
      (async () => {
        try {
          const response = await walletApi.verifyPayment(transactionId);
          if (response.data?.status === 'completed') {
            setPaymentSuccess(true);
            await fetchData();
          }
        } catch (error) {
          console.error('Error verifying transactionId:', error);
        }
      })();
    }
  }, [location]);

  // Auto-refresh codes every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (subscriptions.length > 0) {
        refreshCodes();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [subscriptions]);

  const refreshCodes = async () => {
    try {
      const codesPromises = subscriptions.map(async (sub) => {
        if (sub.status === 'active' || sub.status === 'canceled') {
          try {
            const codesRes = await subscriptionsApi.getCodes(sub.id);
            return { id: sub.id, codes: codesRes.data };
          } catch { return { id: sub.id, codes: [] }; }
        }
        return { id: sub.id, codes: [] };
      });
      const codesResults = await Promise.all(codesPromises);
      const newCodesMap = {};
      codesResults.forEach(({ id, codes }) => { newCodesMap[id] = codes; });
      setCodesMap(newCodesMap);
    } catch (error) { console.error('Error refreshing codes:', error); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [walletRes, txRes, subsRes, typesRes] = await Promise.all([
        walletApi.getWallet(),
        walletApi.getTransactions(),
        subscriptionsApi.getMySubscriptions(),
        subscriptionTypesApi.getAll()
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data);
      setSubscriptions(subsRes.data);
      setSubscriptionTypes(typesRes.data);

      const codesPromises = subsRes.data.map(async (sub) => {
        if (sub.status === 'active' || sub.status === 'canceled') {
          try {
            const codesRes = await subscriptionsApi.getCodes(sub.id);
            return { id: sub.id, codes: codesRes.data };
          } catch { return { id: sub.id, codes: [] }; }
        }
        return { id: sub.id, codes: [] };
      });
      const codesResults = await Promise.all(codesPromises);
      const newCodesMap = {};
      codesResults.forEach(({ id, codes }) => { newCodesMap[id] = codes; });
      setCodesMap(newCodesMap);
    } catch (error) { console.error('Error fetching data:', error); }
    finally { setLoading(false); }
  };

  const fetchDepartments = async () => {
    try { const response = await userApi.getDepartments(); setDepartments(response.data.departments); }
    catch (error) { console.error('Error:', error); }
  };

  const handleShowSubscribe = async (typeId) => {
    const type = subscriptionTypes.find(t => t.id === typeId);
    if (type) { setSelectedSubscription(type); setSubscribeDialogOpen(true); }
  };

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount <= 0) { toast.error('Veuillez entrer un montant valide'); return; }
    setDepositLoading(true);
    try {
      const response = await walletApi.initiateDeposit(amount);
      // Store order_id for payment verification after redirect
      localStorage.setItem('pending_order_id', response.data.order_id);
      window.location.href = response.data.payment_url;
    } catch (error) { toast.error(t('errors.generic')); }
    finally { setDepositLoading(false); }
  };

  const handleSubscribe = async () => {
    if (!selectedSubscription) return;
    if (!wallet || wallet.balance < selectedSubscription.price) { toast.error(t('errors.insufficientBalance')); return; }
    setSubscribeLoading(true);
    try {
      await subscriptionsApi.subscribe(selectedSubscription.id);
      toast.success(t('common.success'));
      setSubscribeDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('errors.generic')); }
    finally { setSubscribeLoading(false); }
  };

  const handleCancelSubscription = async () => {
    if (!subscriptionToCancel) return;
    try {
      await subscriptionsApi.cancel(subscriptionToCancel.id);
      toast.success(t('common.success'));
      setCancelDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(t('errors.generic')); }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleProfileUpdate = async () => {
    setProfileLoading(true);
    try {
      const response = await userApi.updateProfile(profileData);
      updateUser(response.data);
      toast.success(t('common.success'));
      setProfileEditing(false);
    } catch (error) { toast.error(t('errors.generic')); }
    finally { setProfileLoading(false); }
  };

  const handleLanguageChange = async (lang) => {
    i18n.changeLanguage(lang);
    try { await userApi.updateProfile({ preferred_language: lang }); }
    catch (error) { console.error('Error:', error); }
  };

  // Password change handler
  const handleChangePassword = async () => {
    if (newPassword !== confirmNewPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await authApi.changePassword(oldPassword, newPassword);
      toast.success('Mot de passe modifié avec succès');
      setPasswordDialogOpen(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Email change handlers
  const handleRequestEmailChange = async () => {
    if (!newEmail || newEmail === user?.email) {
      toast.error('Veuillez entrer un nouvel email différent');
      return;
    }
    
    setEmailLoading(true);
    try {
      await authApi.requestEmailChange(newEmail);
      setEmailStep('verify');
      toast.success('Un code de vérification a été envoyé à votre email actuel');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la demande');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleConfirmEmailChange = async () => {
    if (emailCode.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres');
      return;
    }
    
    setEmailLoading(true);
    try {
      const response = await authApi.confirmEmailChange(emailCode, newEmail);
      toast.success('Email modifié avec succès');
      setEmailDialogOpen(false);
      setEmailStep('request');
      setNewEmail('');
      setEmailCode('');
      // Update local user data
      if (response.data.new_email) {
        updateUser({ ...user, email: response.data.new_email });
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setEmailLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">{t('dashboard.subscriptions.status.active')}</Badge>;
      case 'canceled': return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">{t('dashboard.subscriptions.status.canceled')}</Badge>;
      case 'stopped': return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">{t('dashboard.subscriptions.status.stopped')}</Badge>;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <span className="font-bold text-muted-foreground">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background" data-testid="user-dashboard">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 bg-white border-r-2 border-border flex-col z-50">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <Link to="/" className="block">
            <img src={logoIzyko} alt="izyko" className="h-12 w-auto object-contain" />
          </Link>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all ${
                    activeTab === item.id 
                      ? 'bg-primary text-white shadow-lg' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className="h-5 w-5" strokeWidth={activeTab === item.id ? 2.5 : 2} />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User info & logout */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <User className="h-5 w-5 text-primary" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="w-full rounded-xl justify-start" 
            onClick={() => { logout(); navigate('/'); }}
            data-testid="dashboard-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" strokeWidth={2} />
            {t('nav.logout')}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 glass border-b-2 border-border/40 px-4 py-4 safe-top">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-xl hover:bg-muted"
          >
            <Menu className="h-6 w-6" strokeWidth={2} />
          </button>
          <img src={logoIzyko} alt="izyko" className="h-12 w-auto object-contain" />
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <User className="h-5 w-5" strokeWidth={2} />
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white flex flex-col animate-in slide-in-from-left">
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <img src={logoIzyko} alt="izyko" className="h-12 w-auto object-contain" />
              <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-xl hover:bg-muted">
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
            
            {/* User info */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" strokeWidth={2} />
                </div>
                <div>
                  <p className="font-bold">{user?.first_name} {user?.last_name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            </div>
            
            {/* Navigation */}
            <nav className="flex-1 p-4">
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all ${
                        activeTab === item.id 
                          ? 'bg-primary text-white' 
                          : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      <item.icon className="h-5 w-5" strokeWidth={2} />
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Logout */}
            <div className="p-4 border-t border-border">
              <Button 
                variant="outline" 
                className="w-full rounded-xl justify-start" 
                onClick={() => { logout(); navigate('/'); }}
              >
                <LogOut className="h-4 w-4 mr-2" strokeWidth={2} />
                {t('nav.logout')}
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-8">
          {/* Welcome Header - Desktop */}
          <div className="hidden lg:flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl font-black">{t('dashboard.welcome')}, {user?.first_name}!</h1>
              <p className="text-muted-foreground font-medium">Gérez vos abonnements et votre portefeuille</p>
            </div>
          </div>

          {/* Wallet Card */}
          {paymentSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
              <p className="text-xs text-green-700 text-center font-medium">
                Transaction réussie !
              </p>
            </div>
          )}
          <div className="wallet-card mb-4" data-testid="wallet-card">
            <div className="relative z-10">
              <p className="text-sm font-bold opacity-70 mb-1">{t('dashboard.wallet.balance')}</p>
              <p className="font-heading text-4xl md:text-5xl font-black mb-6">
                {formatCurrency(wallet?.balance || 0)}
              </p>
              {/* <Button 
                variant="secondary" 
                className="rounded-full shadow-lg"
                onClick={() => setDepositDialogOpen(true)}
                data-testid="add-funds-btn"
              >
                <Plus className="h-5 w-5 mr-2" strokeWidth={2} />
                {t('dashboard.wallet.addFunds')}
              </Button> */}
            </div>
          </div>

          {/* WhatsApp Support Card */}
          <a 
            href="https://wa.me/message/R7W5ZWR4YGJXI1" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block mb-6 md:mb-8"
            data-testid="whatsapp-support-btn"
          >
            <div className="bg-[#25D366]/10 border-2 border-[#25D366]/30 rounded-2xl p-4 flex items-center gap-4 hover:bg-[#25D366]/20 transition-colors">
              <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="h-6 w-6 text-white" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#128C7E]">Besoin d'aide ?</p>
                <p className="text-sm text-muted-foreground">Contactez-nous sur WhatsApp pour toute question ou problème.</p>
              </div>
              <ChevronRight className="h-5 w-5 text-[#25D366] flex-shrink-0" strokeWidth={2} />
            </div>
          </a>

          {/* Content Sections */}
          <div className="space-y-6 md:space-y-8">
            {/* Active Subscriptions - Home Tab */}
            {activeTab === 'home' && (
              <>
                <section>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-xl font-black">{t('dashboard.subscriptions.active')}</h2>
                  </div>
                  
                  {subscriptions.filter(s => s.status !== 'stopped').length === 0 ? (
                    <div className="bg-white rounded-3xl p-8 text-center shadow-soft border-2 border-border">
                      <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Tv className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
                      </div>
                      <p className="text-muted-foreground font-medium mb-4">{t('dashboard.subscriptions.noSubscriptions')}</p>
                      <Button className="rounded-full" onClick={() => setActiveTab('browse')}>
                        {t('dashboard.subscriptions.browse')}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {subscriptions.filter(s => s.status !== 'stopped').map((sub) => (
                        <div key={sub.id} className="bg-white rounded-3xl p-6 shadow-soft border-2 border-border" data-testid={`my-subscription-${sub.id}`}>
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-heading text-lg font-bold">{sub.subscription_type_name}</h3>
                                {getStatusBadge(sub.status)}
                              </div>
                              <p className="text-sm text-muted-foreground font-medium">
                                {t('dashboard.subscriptions.renewalDate')}: {formatDate(sub.renewal_date)}
                              </p>
                            </div>
                            {sub.status === 'active' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="rounded-full text-xs"
                                onClick={() => { setSubscriptionToCancel(sub); setCancelDialogOpen(true); }}
                              >
                                {t('dashboard.subscriptions.cancel')}
                              </Button>
                            )}
                          </div>

                          {/* Credentials */}
                          <div className="bg-muted/50 rounded-2xl p-4 space-y-3">
                            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">{t('dashboard.subscriptions.credentials')}</p>
                            
                            {/* Warning message */}
                            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                              <p className="text-xs text-red-700 text-center font-medium">
                                {t('dashboard.subscriptions.credentialsWarning')}
                              </p>
                            </div>
                            
                            <div className="grid gap-3">
                              <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3">
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs text-muted-foreground font-medium">{t('dashboard.subscriptions.email')}</p>
                                  <p className="font-mono font-bold text-sm truncate">{sub.account_email}</p>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="rounded-full flex-shrink-0"
                                  onClick={() => handleCopy(sub.account_email, `email-${sub.id}`)}
                                >
                                  {copiedField === `email-${sub.id}` ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" strokeWidth={2} />}
                                </Button>
                              </div>
                              <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3">
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs text-muted-foreground font-medium">{t('dashboard.subscriptions.password')}</p>
                                  <p className="font-mono font-bold text-sm truncate">{sub.account_password}</p>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="rounded-full flex-shrink-0"
                                  onClick={() => handleCopy(sub.account_password, `password-${sub.id}`)}
                                >
                                  {copiedField === `password-${sub.id}` ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" strokeWidth={2} />}
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Codes */}
                          {codesMap[sub.id]?.length > 0 ? (
                            <div className="mt-4 bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-4">
                              <div className="flex items-center gap-2 mb-4">
                                <Clock className="h-4 w-4 text-yellow-600" strokeWidth={2} />
                                <p className="text-sm font-bold text-yellow-800">{t('dashboard.subscriptions.codes')}</p>
                              </div>
                              <div className="space-y-3">
                                {codesMap[sub.id].map((code) => (
                                  <div key={code.id} className="flex items-center justify-between bg-white rounded-xl px-4 py-4 shadow-sm border border-yellow-100">
                                    <code className="font-mono font-black text-xl tracking-wider">{code.code}</code>
                                    <div className="flex items-center gap-3">
                                      <span className="text-xs text-muted-foreground font-medium">
                                        {getTimeRemaining(code.expires_at)} min
                                      </span>
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        className="rounded-full"
                                        onClick={() => handleCopy(code.code, `code-${code.id}`)}
                                      >
                                        {copiedField === `code-${code.id}` ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" strokeWidth={2} />}
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-3">
                              <p className="text-xs text-blue-700 text-center">
                                <span className="font-semibold">Info :</span> {t('dashboard.subscriptions.codesInfo')}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {/* Quick actions */}
                <section>
                  <h2 className="font-heading text-xl font-black mb-4">{t('pricing.title')}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {subscriptionTypes.slice(0, 4).map((type) => {
                      const hasActive = subscriptions.some(s => s.subscription_type_id === type.id && (s.status === 'active' || s.status === 'canceled'));
                      return (
                        <button
                          key={type.id}
                          onClick={() => { if (!hasActive && type.is_active) { setSelectedSubscription(type); setSubscribeDialogOpen(true); }}}
                          disabled={hasActive || !type.is_active}
                          className="bg-white rounded-2xl p-4 text-left shadow-soft border-2 border-border hover:border-primary/30 transition-all disabled:opacity-50"
                        >
                          <img src={type.icon_url} alt={type.name} className="w-10 h-10 object-contain rounded-xl mb-3" />
                          <h3 className="font-bold text-sm mb-1">{type.name}</h3>
                          <p className="font-heading font-black">{formatCurrency(type.price)}</p>
                        </button>
                      );
                    })}
                  </div>
                  {subscriptionTypes.length > 4 && (
                    <Button variant="outline" className="w-full mt-4 rounded-full" onClick={() => setActiveTab('browse')}>
                      Voir tout <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </section>

                {/* Recent transactions */}
                <section>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-xl font-black">{t('dashboard.wallet.transactions')}</h2>
                    <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setActiveTab('transactions')}>
                      Voir tout
                    </Button>
                  </div>
                  {transactions.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8 font-medium">{t('dashboard.wallet.noTransactions')}</p>
                  ) : (
                    <div className="bg-white rounded-3xl shadow-soft border-2 border-border overflow-hidden">
                      {transactions.slice(0, 5).map((tx) => (
                        <div key={tx.id} className="flex items-center justify-between px-4 py-4 border-b border-border/50 last:border-0">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tx.amount > 0 ? 'bg-green-100' : 'bg-red-50'}`}>
                              {tx.amount > 0 ? <ArrowDownLeft className="h-5 w-5 text-green-600" strokeWidth={2} /> : <ArrowUpRight className="h-5 w-5 text-red-500" strokeWidth={2} />}
                            </div>
                            <div>
                              <p className="font-bold text-sm">{tx.description}</p>
                              <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                            </div>
                          </div>
                          <p className={`font-mono font-bold ${tx.amount > 0 ? 'text-green-600' : ''}`}>
                            {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}

            {/* Browse Tab */}
            {activeTab === 'browse' && (
              <section>
                <h2 className="font-heading text-xl font-black mb-4">{t('pricing.title')}</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {subscriptionTypes.map((type) => {
                    const hasActive = subscriptions.some(s => s.subscription_type_id === type.id && (s.status === 'active' || s.status === 'canceled'));
                    return (
                      <div key={type.id} className="bg-white rounded-3xl p-6 shadow-soft border-2 border-border hover:shadow-medium transition-all">
                        <div className="flex items-start justify-between mb-4">
                        <img src={type.icon_url} alt={type.name} className="w-14 h-14 object-contain rounded-2xl" />
                          {!type.is_active && <Badge variant="secondary">Indisponible</Badge>}
                        </div>
                        <h3 className="font-heading text-xl font-bold mb-1">{type.name}</h3>
                        {type.description && <p className="text-sm text-muted-foreground mb-4">{type.description}</p>}
                        <p className="font-heading text-3xl font-black mb-4">{formatCurrency(type.price)}<span className="text-base font-medium text-muted-foreground">/mois</span></p>
                        <Button 
                          className="w-full rounded-full"
                          disabled={hasActive || !type.is_active}
                          onClick={() => { setSelectedSubscription(type); setSubscribeDialogOpen(true); }}
                        >
                          {hasActive ? 'Déjà abonné' : type.is_active ? t('pricing.subscribe') : t('pricing.unavailable')}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <section>
                <h2 className="font-heading text-xl font-black mb-4">{t('dashboard.wallet.transactions')}</h2>
                <div className="bg-white rounded-3xl shadow-soft border-2 border-border overflow-hidden">
                  {transactions.length === 0 ? (
                    <p className="text-center text-muted-foreground py-12 font-medium">{t('dashboard.wallet.noTransactions')}</p>
                  ) : (
                    transactions.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between px-4 py-4 border-b border-border/50 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tx.amount > 0 ? 'bg-green-100' : 'bg-red-50'}`}>
                            {tx.amount > 0 ? <ArrowDownLeft className="h-5 w-5 text-green-600" strokeWidth={2} /> : <ArrowUpRight className="h-5 w-5 text-red-500" strokeWidth={2} />}
                          </div>
                          <div>
                            <p className="font-bold text-sm">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                          </div>
                        </div>
                        <p className={`font-mono font-bold ${tx.amount > 0 ? 'text-green-600' : ''}`}>
                          {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </section>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <section>
                <h2 className="font-heading text-xl font-black mb-4">{t('dashboard.profile.title')}</h2>
                <div className="bg-white rounded-3xl p-6 shadow-soft border-2 border-border">
                  {profileEditing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="font-bold">{t('auth.register.firstName')}</Label>
                          <Input value={profileData.first_name || ''} onChange={(e) => setProfileData({...profileData, first_name: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-bold">{t('auth.register.lastName')}</Label>
                          <Input value={profileData.last_name || ''} onChange={(e) => setProfileData({...profileData, last_name: e.target.value})} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-bold">{t('auth.register.phone')}</Label>
                        <Input value={profileData.phone || ''} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="font-bold">{t('auth.register.department')}</Label>
                          <Select value={profileData.department || ''} onValueChange={(v) => setProfileData({...profileData, department: v})}>
                            <SelectTrigger className="rounded-xl h-12"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                            <SelectContent className="rounded-xl">
                              {departments.map((dept) => (<SelectItem key={dept} value={dept} className="rounded-lg">{dept}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="font-bold">{t('auth.register.city')}</Label>
                          <Input value={profileData.city || ''} onChange={(e) => setProfileData({...profileData, city: e.target.value})} />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" className="rounded-full" onClick={() => setProfileEditing(false)}>{t('common.cancel')}</Button>
                        <Button className="rounded-full" onClick={handleProfileUpdate} disabled={profileLoading}>
                          {profileLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center shrink-0">
                            <User className="h-8 w-8 text-primary" strokeWidth={1.5} />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-heading text-xl font-bold truncate">{user?.first_name} {user?.last_name}</h3>
                            <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                          </div>
                        </div>
                        <Button variant="outline" className="rounded-full shrink-0 self-start sm:self-auto" onClick={() => { setProfileData(user); setProfileEditing(true); }}>
                          {t('common.edit')}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-muted/50 rounded-xl p-4">
                          <p className="text-xs text-muted-foreground font-bold uppercase">{t('auth.register.phone')}</p>
                          <p className="font-bold">{user?.phone || '-'}</p>
                        </div>
                        <div className="bg-muted/50 rounded-xl p-4">
                          <p className="text-xs text-muted-foreground font-bold uppercase">{t('auth.register.department')}</p>
                          <p className="font-bold">{user?.department || '-'}</p>
                        </div>
                        <div className="bg-muted/50 rounded-xl p-4">
                          <p className="text-xs text-muted-foreground font-bold uppercase">{t('auth.register.city')}</p>
                          <p className="font-bold">{user?.city || '-'}</p>
                        </div>
                      </div>
                      <div className="mt-6 pt-6 border-t border-border">
                        <p className="text-xs text-muted-foreground font-bold uppercase mb-3">{t('dashboard.profile.language')}</p>
                        <div className="flex gap-2">
                          {[{ code: 'fr', name: 'Français' }, { code: 'ht', name: 'Kreyòl' }, { code: 'en', name: 'English' }].map((lang) => (
                            <Button key={lang.code} variant={i18n.language === lang.code ? 'default' : 'outline'} className="rounded-full" onClick={() => handleLanguageChange(lang.code)}>
                              {lang.name}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Security Section */}
                      <div className="mt-6 pt-6 border-t border-border">
                        <p className="text-xs text-muted-foreground font-bold uppercase mb-3">Sécurité</p>
                        <div className="space-y-3">
                          <button
                            onClick={() => setPasswordDialogOpen(true)}
                            className="w-full flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors"
                            data-testid="change-password-btn"
                          >
                            <div className="flex items-center gap-3">
                              <KeyRound className="h-5 w-5 text-muted-foreground" strokeWidth={2} />
                              <span className="font-bold">Changer le mot de passe</span>
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground" strokeWidth={2} />
                          </button>
                          <button
                            onClick={() => { setNewEmail(''); setEmailStep('request'); setEmailDialogOpen(true); }}
                            className="w-full flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors"
                            data-testid="change-email-btn"
                          >
                            <div className="flex items-center gap-3">
                              <Mail className="h-5 w-5 text-muted-foreground" strokeWidth={2} />
                              <span className="font-bold">Changer l'email</span>
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground" strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-6 pt-6 border-t border-border lg:hidden">
                        <Button variant="outline" className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => { logout(); navigate('/'); }}>
                          <LogOut className="h-4 w-4 mr-2" strokeWidth={2} />
                          {t('nav.logout')}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t-2 border-border z-[60] px-2 py-2 safe-bottom">
        <div className="flex items-center justify-around pb-2">
          {navItems.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-colors ${activeTab === tab.id ? 'text-primary bg-primary/10' : 'text-muted-foreground'}`}
            >
              <tab.icon className="h-5 w-5" strokeWidth={activeTab === tab.id ? 2.5 : 2} />
              <span className="text-xs font-bold">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Deposit Dialog */}
      <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-xl">{t('dashboard.wallet.addFunds')}</DialogTitle>
            <DialogDescription>Entrez le montant à recharger via MonCash</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-bold">{t('dashboard.wallet.amount')} (HTG)</Label>
              <Input type="number" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder="1000" min="100" data-testid="deposit-amount-input" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[500, 1000, 2000].map((amount) => (
                <Button key={amount} variant="outline" className="rounded-xl" onClick={() => setDepositAmount(amount.toString())}>
                  {amount} HTG
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setDepositDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleDeposit} disabled={depositLoading} className="rounded-full bg-[#E31837] hover:bg-[#c71530]" data-testid="deposit-confirm-btn">
              {depositLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2" />{t('dashboard.wallet.payWith')}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscribe Dialog */}
      <Dialog open={subscribeDialogOpen} onOpenChange={setSubscribeDialogOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-xl">S'abonner à {selectedSubscription?.name}</DialogTitle>
            <DialogDescription>Confirmez votre abonnement</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-muted/50 rounded-2xl p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Prix mensuel</span>
                <span className="font-heading font-black text-xl">{formatCurrency(selectedSubscription?.price || 0)}</span>
              </div>
              <div className="flex justify-between items-center text-sm text-muted-foreground">
                <span>Solde actuel</span>
                <span className="font-bold">{formatCurrency(wallet?.balance || 0)}</span>
              </div>
            </div>
            {wallet && selectedSubscription && wallet.balance < selectedSubscription.price && (
              <p className="text-destructive text-sm font-bold">{t('errors.insufficientBalance')}</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setSubscribeDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSubscribe} disabled={subscribeLoading || (wallet && selectedSubscription && wallet.balance < selectedSubscription.price)} className="rounded-full" data-testid="confirm-subscribe-btn">
              {subscribeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-xl">{t('dashboard.subscriptions.cancelConfirm')}</DialogTitle>
            <DialogDescription>{t('dashboard.subscriptions.cancelWarning')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setCancelDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" className="rounded-full" onClick={handleCancelSubscription}>{t('common.confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-xl">Changer le mot de passe</DialogTitle>
            <DialogDescription>Entrez votre ancien mot de passe puis choisissez un nouveau</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-bold">Ancien mot de passe</Label>
              <div className="relative">
                <Input
                  type={showPasswords ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="pr-12"
                  data-testid="old-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-bold">Nouveau mot de passe</Label>
              <Input
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-bold">Confirmer le nouveau mot de passe</Label>
              <Input
                type={showPasswords ? 'text' : 'password'}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                data-testid="confirm-new-password-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => { setPasswordDialogOpen(false); setOldPassword(''); setNewPassword(''); setConfirmNewPassword(''); }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleChangePassword} disabled={passwordLoading || !oldPassword || !newPassword} className="rounded-full" data-testid="confirm-password-change-btn">
              {passwordLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Changer le mot de passe'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={(open) => { if (!open) { setEmailStep('request'); setNewEmail(''); setEmailCode(''); } setEmailDialogOpen(open); }}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-xl">
              {emailStep === 'request' ? 'Changer l\'email' : 'Vérification'}
            </DialogTitle>
            <DialogDescription>
              {emailStep === 'request' 
                ? 'Entrez votre nouvelle adresse email' 
                : `Un code a été envoyé à ${user?.email}`}
            </DialogDescription>
          </DialogHeader>
          
          {emailStep === 'request' ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="font-bold">Nouvel email</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="nouveau@email.com"
                  data-testid="new-email-input"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Un code de confirmation sera envoyé à votre email actuel ({user?.email})
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="font-bold">Code de vérification</Label>
                <Input
                  type="text"
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-2xl font-mono tracking-[0.5em]"
                  data-testid="email-change-code-input"
                />
              </div>
              <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <p className="text-sm text-yellow-800">
                  Si vous ne trouvez pas l'email, <strong>vérifiez vos spams</strong>.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => { setEmailDialogOpen(false); setEmailStep('request'); setNewEmail(''); setEmailCode(''); }}>
              {t('common.cancel')}
            </Button>
            {emailStep === 'request' ? (
              <Button onClick={handleRequestEmailChange} disabled={emailLoading || !newEmail} className="rounded-full" data-testid="request-email-change-btn">
                {emailLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Envoyer le code'}
              </Button>
            ) : (
              <Button onClick={handleConfirmEmailChange} disabled={emailLoading || emailCode.length !== 6} className="rounded-full" data-testid="confirm-email-change-btn">
                {emailLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmer le changement'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
